/**
 * 草稿箱批量发布：每条草稿走与发品页相同的规则/属性/SKU 校验，再提交 publishOrEdit。
 * 平台预检约 40/s；发品提交安全上限 35/s（同店）。草稿箱批量仍串行提交以对齐同店 FIFO。
 */

import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import { tiktokPlatformAPI } from '@/utils/api';
import { parseAttributeTemplate, resolveSkcColorAgainstTemplate } from '@/tiktok-platform/utils/tiktokAttributeTemplate';
import { buildTiktokPublishPayload, samplePublishPayloadCurrency } from '@/tiktok-platform/utils/buildTiktokPublishPayload';
import { runTiktokListingPlatformCheck } from '@/tiktok-platform/utils/tiktokListingCheck';
import { isTiktokTtsPublishMode } from '@/tiktok-platform/utils/tiktokPublishUiMode';
import {
  resolveSkuPackageCapabilities,
} from '@/tiktok-platform/utils/tiktokPublishFillInStandard';
import { resolveShopPublishCurrencies } from '@/tiktok-platform/utils/resolveTiktokPublishCurrency';
import { runTiktokPublishPreflight } from '@/tiktok-platform/utils/tiktokPublishPreflight';
import { extractTiktokPublishFailMessage, extractTiktokPublishedSpu, isTiktokPublishSuccess } from '@/tiktok-platform/utils/tiktokPublishResult';
import { pruneSizeChartForSubmit, skuSizeKey } from '@/tiktok-platform/utils/tiktokSizeChart';
import { imageUriForTts } from '@/tiktok-platform/utils/buildTiktokTtsCreateProductPayload';
import {
  officialSizeChartDraftHasContent,
  resolveOfficialSizeChartSeedRecords,
  resolveOfficialSizeTemplateIds,
} from '@/tiktok-platform/utils/tiktokOfficialSizeChart';
import { TIKTOK_SIZE_CHART_IMAGE_USE_CASE } from '@/tiktok-platform/utils/tiktokProductImage';
import { convertListingImageToTiktok } from '@/pages/ipe/workbench/utils/convertListingImageToTiktok';
import { validateTiktokPublishForm } from '@/tiktok-platform/utils/validateTiktokPublishForm';
import { translateTiktokTitleToEnglish } from '@/tiktok-platform/utils/tiktokPublishAiTitle';
import { alignAttributesToCategoryTemplate } from '@/tiktok-platform/utils/tiktokProductAttributeTemplate';
import {
  enrichTiktokFormPrintNames,
  filterSkcsReadyForPlatformPublish,
  LISTING_NO_PUBLISHABLE_COLOR_MESSAGE,
  printNamesFromTiktokPrintImages,
} from '@/pages/ipe/workbench/utils/applyListingItemToTiktokPublishForm';
import {
  EMPTY_TIKTOK_PRINT_INFRINGEMENT,
  normalizeTiktokPrintInfringement,
} from '@/tiktok-platform/utils/tiktokPublishPrintInfringement';
import { filterTiktokSalesWarehouses } from '@/tiktok-platform/utils/tiktokSalesWarehouse';
import {
  EMPTY_TIKTOK_CATEGORY_RULES,
  normalizeTiktokCategoryRules,
} from '@/tiktok-platform/utils/tiktokCategoryRules';
import {
  createTiktokIdempotencyKey,
  tiktokMultilineText,
} from '@/tiktok-platform/utils/tiktokPublishFormLimits';
import { firstFilledSkuPackageField } from '@/tiktok-platform/utils/tiktokPackageBatch';
import { resolveTiktokProductLevelImages } from '@/tiktok-platform/utils/tiktokProductImage';

/** 预检等读接口并发上限（平台约 40/s） */
export const TIKTOK_BATCH_PUBLISH_CONCURRENCY = 40;

/** 同店发品提交安全上限：平台约 40/s，预留余量用 35 */
export const TIKTOK_PUBLISH_SUBMIT_MAX_PER_SECOND = 35;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * 滑动窗口限流：同一店铺每秒最多 acquire maxPerSecond 次。
 * @param {number} maxPerSecond
 */
export function createPerSecondRateLimiter(maxPerSecond = TIKTOK_PUBLISH_SUBMIT_MAX_PER_SECOND) {
  const limit = Math.max(1, Number(maxPerSecond) || TIKTOK_PUBLISH_SUBMIT_MAX_PER_SECOND);
  const stamps = [];
  return {
    async acquire() {
      while (true) {
        const now = Date.now();
        while (stamps.length && now - stamps[0] >= 1000) stamps.shift();
        if (stamps.length < limit) {
          stamps.push(now);
          return;
        }
        const waitMs = Math.max(1, 1000 - (now - stamps[0]));
        // eslint-disable-next-line no-await-in-loop
        await sleep(waitMs);
      }
    },
  };
}

function deriveFormCurrency(initial = {}) {
  const top = String(initial.currency || '').trim();
  if (top) return top;
  for (const skc of initial.skcList || []) {
    for (const sku of skc.skuRows || []) {
      const code = String(sku?.currency || '').trim();
      if (code) return code;
    }
  }
  return '';
}

function deriveSelectedSizeIds(skcList = []) {
  const ids = [];
  const seen = new Set();
  (skcList || []).forEach((skc) => {
    (skc.skuRows || []).forEach((sku) => {
      const key = skuSizeKey(sku);
      if (key && !seen.has(key)) {
        seen.add(key);
        ids.push(key);
      }
    });
  });
  return ids;
}

function normalizeProductAttributes(raw = {}) {
  const out = {};
  Object.entries(raw || {}).forEach(([id, value]) => {
    if (value == null || value === '') return;
    if (Array.isArray(value)) {
      const asComposition = value.some((row) => (
        row && typeof row === 'object' && ('percent' in row || 'attributeExtraValue' in row || 'valueId' in row)
      ));
      if (asComposition) {
        out[id] = value
          .map((row) => {
            if (row != null && (typeof row === 'string' || typeof row === 'number')) {
              return { valueId: String(row), percent: '' };
            }
            if (!row || typeof row !== 'object') return null;
            const valueId = row.valueId ?? row.attributeValueId;
            if (valueId == null || valueId === '') return null;
            return {
              valueId: String(valueId),
              percent: String(row.percent ?? row.attributeExtraValue ?? ''),
            };
          })
          .filter(Boolean);
        return;
      }
      out[id] = value
        .map((row) => {
          if (row && typeof row === 'object') return String(row.valueId ?? row.attributeValueId ?? '');
          return String(row ?? '');
        })
        .filter(Boolean);
      return;
    }
    if (typeof value === 'object') {
      const valueId = value.valueId ?? value.attributeValueId;
      const percent = value.percent ?? value.attributeExtraValue;
      if (valueId != null && valueId !== '') {
        if (percent != null && String(percent).trim() !== '') {
          out[id] = [{ valueId: String(valueId), percent: String(percent) }];
        } else {
          out[id] = String(valueId);
        }
      } else if (value.attributeExtraValue != null && String(value.attributeExtraValue).trim() !== '') {
        out[id] = String(value.attributeExtraValue);
      }
      return;
    }
    out[id] = value;
  });
  return out;
}

/** 组包前规范化表单；必须保留站内印花图，供编辑页回填。 */
export function emptyPublishForm(initial = {}) {
  const rawTitle = String(initial.title || initial.productName || '').trim();
  const rawTitleEn = String(initial.titleEn || initial.englishTitle || '').trim();
  const titleLooksEnglishOnly = rawTitle && !/[\u3400-\u9fff]/.test(rawTitle);
  return {
    spuName: initial.spuName || '',
    title: rawTitle,
    titleEn: rawTitleEn || (titleLooksEnglishOnly ? rawTitle : ''),
    description: initial.description || '',
    categoryId: initial.categoryId || '',
    productTypeId: initial.productTypeId || '',
    categoryPathLabel: initial.categoryPathLabel || '',
    supplierCode: initial.supplierCode || '',
    brandCode: initial.brandCode || '',
    originCountry: initial.originCountry || '',
    ipId: initial.ipId || '',
    ipName: initial.ipName || '',
    ipNameCn: initial.ipNameCn || '',
    productAttributes: normalizeProductAttributes(initial.productAttributes || {}),
    skcList: Array.isArray(initial.skcList) ? initial.skcList : [],
    selectedSizeIds: Array.isArray(initial.selectedSizeIds)
      ? initial.selectedSizeIds
      : deriveSelectedSizeIds(initial.skcList),
    ...(() => {
      const imgs = resolveTiktokProductLevelImages(initial);
      return { mainImage: imgs.mainImage, detailImages: imgs.detailImages };
    })(),
    sizeChart: initial.sizeChart || {},
    sizeChartImage: initial.sizeChartImage || initial.tiktokSizeChartImage || null,
    tiktokSizeTemplateIds: Array.isArray(initial.tiktokSizeTemplateIds)
      ? initial.tiktokSizeTemplateIds.map((id) => String(id || '').trim()).filter(Boolean)
      : [],
    officialSizeChart: initial.officialSizeChart && typeof initial.officialSizeChart === 'object'
      ? initial.officialSizeChart
      : null,
    sizeChartData: initial.sizeChartData && typeof initial.sizeChartData === 'object'
      ? initial.sizeChartData
      : null,
    selectedSizeChartTemplateId: String(initial.selectedSizeChartTemplateId || '').trim(),
    sizeSaleAttributeId: initial.sizeSaleAttributeId || '',
    sizeValueLabels: initial.sizeValueLabels && typeof initial.sizeValueLabels === 'object'
      ? initial.sizeValueLabels
      : {},
    // 站内印花图：任务刊登必须带入编辑快照，不能在组包时丢掉
    printImages: Array.isArray(initial.printImages) ? initial.printImages : [],
    ipePrintImageNames: Array.isArray(initial.ipePrintImageNames)
      ? initial.ipePrintImageNames
      : printNamesFromTiktokPrintImages(initial.printImages),
    ipePrintInfringement: normalizeTiktokPrintInfringement(
      initial.ipePrintInfringement || EMPTY_TIKTOK_PRINT_INFRINGEMENT,
    ),
    supplyPrice: initial.supplyPrice || '',
    currency: deriveFormCurrency(initial),
    stockQty: initial.stockQty || '',
    // 商品级包裹：优先表单字段；旧草稿只写在 SKU 行时回填
    weightGrams: initial.weightGrams || firstFilledSkuPackageField(initial.skcList, 'weight') || '',
    weightUnit: initial.weightUnit || 'g',
    pkgLength: initial.pkgLength || firstFilledSkuPackageField(initial.skcList, 'length') || '10',
    pkgWidth: initial.pkgWidth || firstFilledSkuPackageField(initial.skcList, 'width') || '10',
    pkgHeight: initial.pkgHeight || firstFilledSkuPackageField(initial.skcList, 'height') || '10',
    dimUnit: initial.dimUnit || 'cm',
    warehouseId: initial.warehouseId || '',
    warehouseName: initial.warehouseName || '',
    selectedSites: Array.isArray(initial.selectedSites) ? initial.selectedSites : [],
    mainSite: initial.mainSite || 'tiktok',
    /**
     * 上架方式：'1' = 自动上架（立即），'2' = 预约上架。
     * 与 scheduled_sale 的唯一开关 hopeOnSaleDate 保持一致：旧数据只填了时间没写方式时补 '2'。
     */
    shelfWay: initial.shelfWay === '2' || (!initial.shelfWay && initial.hopeOnSaleDate) ? '2' : '1',
    /** 预约上架（scheduled_sale）：datetime-local，空 = 立即上架 */
    hopeOnSaleDate: initial.hopeOnSaleDate || '',
    /** 搜索词 / 商品卖点：多行文本（一行一条） */
    searchTerms: tiktokMultilineText(initial.searchTerms),
    keyProductFeatures: tiktokMultilineText(initial.keyProductFeatures),
    /** 限购：空 = 不限 */
    maxOrderQuantity: initial.maxOrderQuantity != null ? String(initial.maxOrderQuantity) : '',
    cumulativeOrderQuantity: initial.cumulativeOrderQuantity != null
      ? String(initial.cumulativeOrderQuantity)
      : '',
    /** 预售（skus[].pre_sale）：'' = 非预售 */
    preSaleType: initial.preSaleType || '',
    preSaleHandlingDays: initial.preSaleHandlingDays != null ? String(initial.preSaleHandlingDays) : '',
    preSaleReleaseDate: initial.preSaleReleaseDate || '',
  };
}

function sizeChartImageSource(image) {
  if (!image || typeof image !== 'object') return '';
  const candidates = [image.ipeUrl, image.publicUrl, image.previewUrl, image.url, image.tiktokUrl];
  for (const raw of candidates) {
    const url = String(raw || '').trim();
    if (!url || url.startsWith('blob:') || url.startsWith('data:')) continue;
    return url;
  }
  return '';
}

/**
 * 自动刊登组包：带上模板尺码图（转成平台 uri）或尺码表模板 id。
 * emptyPublishForm 以前会丢掉这两项，Create Product 因此报 12052673。
 */
async function ensureTiktokSizeChartForPublish(form, shopId) {
  let next = form;
  const image = next.sizeChartImage || next.tiktokSizeChartImage;
  if (image && typeof image === 'object' && !imageUriForTts(image)) {
    const source = sizeChartImageSource(image);
    if (!source) {
      throw new Error(fixedUi('尺码表图片缺少可上传地址，请在产品模板中重新上传尺码图'));
    }
    const converted = await convertListingImageToTiktok({
      shopId,
      url: source,
      imageType: 1,
      nameHint: image.name || 'size-chart',
      useCase: TIKTOK_SIZE_CHART_IMAGE_USE_CASE,
    });
    const uri = String(converted?.uri || '').trim();
    if (!uri) {
      throw new Error(fixedUi('尺码表图片未转成平台 uri，请重试'));
    }
    next = {
      ...next,
      sizeChartImage: {
        ...image,
        ...converted,
        uri,
        tiktokUri: uri,
      },
    };
  }

  const existingIds = (Array.isArray(next.tiktokSizeTemplateIds) ? next.tiktokSizeTemplateIds : [])
    .map((id) => String(id || '').trim())
    .filter((id) => id && id !== '0');
  if (existingIds.length || imageUriForTts(next.sizeChartImage)) {
    return next;
  }

  let draft = next.officialSizeChart && typeof next.officialSizeChart === 'object'
    ? next.officialSizeChart
    : null;
  if (!officialSizeChartDraftHasContent(draft) && next.sizeChartData && typeof next.sizeChartData === 'object') {
    draft = { mode: 'create', sizeChartData: next.sizeChartData, records: [] };
  }
  const localTemplateId = String(next.selectedSizeChartTemplateId || '').trim();
  if (!officialSizeChartDraftHasContent(draft) && localTemplateId && shopId) {
    let loaded = null;
    let lastError = null;
    for (const scope of ['PERSONAL', 'TEAM']) {
      try {
        const res = await tiktokPlatformAPI.getSizeChartTemplate(shopId, localTemplateId, { scope });
        const item = res?.item || res || null;
        if (item && (item.payload || item.id != null)) {
          loaded = item;
          break;
        }
      } catch (error) {
        lastError = error;
      }
    }
    if (!loaded) {
      throw lastError || new Error(fixedUi('找不到产品模板关联的尺码表'));
    }
    const payload = loaded.payload && typeof loaded.payload === 'object' ? loaded.payload : {};
    draft = payload.officialSizeChart || payload.form?.officialSizeChart || payload;
  }
  if (!officialSizeChartDraftHasContent(draft)) return next;

  const mode = draft.mode === 'template' || draft.mode === 'image' || draft.mode === 'create'
    ? draft.mode
    : 'create';
  if (mode === 'image') return next;
  const groupList = Array.isArray(draft.groupList) ? draft.groupList : [];
  const elementList = Array.isArray(draft.elementList) ? draft.elementList : [];
  const records = resolveOfficialSizeChartSeedRecords(draft, groupList, elementList);
  const ids = await resolveOfficialSizeTemplateIds({
    shopId,
    catId: next.categoryId,
    required: true,
    mode,
    sizeClass: draft.sizeClass || null,
    selectedBusinessIds: draft.selectedBusinessIds || [],
    chartName: '发品尺码表',
    records,
    groupList,
    elementList,
    measurementType: draft.measurementType || draft.metaRes?.measurementType || '',
    api: tiktokPlatformAPI,
  });
  if (!ids?.length) {
    throw new Error(fixedUi('尺码表未生成平台模板，请在产品模板中填写尺码或上传尺码图'));
  }
  return {
    ...next,
    officialSizeChart: draft,
    tiktokSizeTemplateIds: ids.map(String),
  };
}

/**
 * @template T, R
 * @param {T[]} items
 * @param {number} concurrency
 * @param {(item: T, index: number) => Promise<R>} worker
 * @param {(done: number, total: number, result: R, index: number) => void} [onItemDone]
 */
export async function mapWithConcurrency(items, concurrency, worker, onItemDone) {
  const list = Array.isArray(items) ? items : [];
  const results = new Array(list.length);
  let cursor = 0;
  let finished = 0;
  const limit = Math.max(1, Math.min(Number(concurrency) || 1, list.length || 1));

  async function runWorker() {
    while (true) {
      const index = cursor;
      cursor += 1;
      if (index >= list.length) return;
      // eslint-disable-next-line no-await-in-loop
      const result = await worker(list[index], index);
      results[index] = result;
      finished += 1;
      onItemDone?.(finished, list.length, result, index);
    }
  }

  if (!list.length) return results;
  await Promise.all(Array.from({ length: limit }, () => runWorker()));
  return results;
}

/**
 * 批量链路：按末级类目取 Get Category Rules（预售 / 合规等字段均由它驱动）。
 * 与属性模板同款缓存；拉取失败不阻断（返回 EMPTY，由平台裁定）。
 */
async function resolveCategoryRules(shopId, categoryId, cache) {
  const key = String(categoryId);
  if (cache?.has(key)) return cache.get(key);
  const pending = tiktokPlatformAPI.getPublishCategoryRules(shopId, categoryId)
    .then((raw) => normalizeTiktokCategoryRules(raw))
    .catch(() => EMPTY_TIKTOK_CATEGORY_RULES);
  cache?.set(key, pending);
  return pending;
}

async function resolveAttrTemplate(shopId, productTypeId, cache) {
  const key = String(productTypeId);
  if (cache.has(key)) return cache.get(key);
  const pending = tiktokPlatformAPI.getAttributeTemplate(shopId, {
    product_type_id_list: [Number(productTypeId)],
  }).then((raw) => parseAttributeTemplate(raw, { productTypeId }));
  cache.set(key, pending);
  try {
    return await pending;
  } catch (ex) {
    cache.delete(key);
    throw ex;
  }
}

function buildSubmitForm(form, { colorAttr, sizeAttr, alignedCurrency }) {
  const sizeValueLabels = { ...(form.sizeValueLabels || {}) };
  (sizeAttr?.values || []).forEach((v) => {
    if (v?.id != null && v?.name) sizeValueLabels[String(v.id)] = String(v.name);
  });
  const resolvedSizeSaleAttrId = form.sizeSaleAttributeId || sizeAttr?.attributeId || '';
  const fromSkcSizes = deriveSelectedSizeIds(form.skcList).map((id) => String(id));
  const resolvedSelectedSizeIds = fromSkcSizes.length
    ? fromSkcSizes
    : (form.selectedSizeIds || []).map((id) => String(id));

  const submitFormBase = {
    ...form,
    sizeValueLabels,
    sizeSaleAttributeId: resolvedSizeSaleAttrId,
    selectedSizeIds: resolvedSelectedSizeIds,
    spuName: '',
    currency: alignedCurrency || form.currency,
    skcList: (form.skcList || []).map((skc) => {
      const resolved = resolveSkcColorAgainstTemplate({
        ...skc,
        colorAttributeId: skc.colorAttributeId || colorAttr?.attributeId || '',
      }, colorAttr);
      return {
      ...resolved,
      colorLabel: resolved.colorIsCustom || resolved.colorExtraValue
        ? (resolved.colorExtraValue || resolved.colorLabel)
        : (colorAttr?.values?.find((v) => String(v.id) === String(resolved.colorValueId))?.name
          || resolved.colorLabel),
      skcName: '',
      skuRows: (skc.skuRows || []).map((sku) => ({
        ...sku,
        sizeAttributeId: sku.sizeAttributeId || sizeAttr?.attributeId || resolvedSizeSaleAttrId || '',
        sizeLabel: sizeAttr?.values?.find((v) => String(v.id) === String(sku.sizeValueId))?.name
          || sku.sizeLabel
          || sizeValueLabels[String(sku.sizeValueId || '')]
          || '',
        currency: alignedCurrency || sku.currency || form.currency,
        skuCode: '',
      })),
    };
    }),
  };
  return submitFormBase;
}

export async function waitGoodsSubmitJob(shopId, jobId, onHint) {
  let latest = await tiktokPlatformAPI.getGoodsSubmitJob(shopId, jobId);
  while (latest && !latest.done) {
    onHint?.(latest.message || fixedUi('排队等待调用平台…'));
    // eslint-disable-next-line no-await-in-loop
    await sleep(700);
    // eslint-disable-next-line no-await-in-loop
    latest = await tiktokPlatformAPI.getGoodsSubmitJob(shopId, jobId);
  }
  return latest;
}

/**
 * 发品表单：属性模板 → 三步预检 → 本地校验 → 组包（与发品页一致）。
 * @returns {Promise<object>}
 */
function dedupeValidationMessages(messages = []) {
  const seen = new Set();
  const out = [];
  (Array.isArray(messages) ? messages : []).forEach((raw) => {
    const message = String(raw || '').trim();
    if (!message || seen.has(message)) return;
    seen.add(message);
    out.push(message);
  });
  return out;
}

function buildPrepareFailure({
  draftId,
  title,
  stage = 'validate',
  message = '',
  messages = [],
  meta = {},
  field,
  section,
}) {
  const deduped = dedupeValidationMessages([...messages, message]);
  return {
    ok: false,
    draftId,
    title,
    stage,
    message: deduped[0] || fixedUi('准备刊登失败'),
    messages: deduped,
    ...(field ? { field } : {}),
    ...(section ? { section } : {}),
    ...meta,
  };
}

export async function prepareFormPublish({
  shopId,
  form: rawForm,
  titleHint = '',
  draftId = null,
  meta = {},
  warehouses = [],
  attrTplCache,
  categoryRulesCache,
  shopType = '',
  siteList = null,
  omitIncompleteSkcs = false,
  collectAllErrors = false,
}) {
  const validationMessages = [];
  const pushValidationMessage = (message) => {
    const text = String(message || '').trim();
    if (!text) return;
    validationMessages.push(text);
  };
  const failPrepare = (stage, message, extra = {}) => {
    if (collectAllErrors) {
      pushValidationMessage(message);
      return null;
    }
    return buildPrepareFailure({
      draftId,
      title: extra.title,
      stage,
      message,
      messages: validationMessages,
      meta,
      field: extra.field,
      section: extra.section,
    });
  };

  let form = emptyPublishForm(rawForm || {});
  if (omitIncompleteSkcs) {
    const skcList = filterSkcsReadyForPlatformPublish(form.skcList);
    form = {
      ...form,
      skcList,
      selectedSizeIds: deriveSelectedSizeIds(skcList),
    };
    if (!skcList.length) {
      const titleEarly = String(form.title || titleHint || meta.title || '').trim() || fixedUi('未命名商品');
      const earlyFail = failPrepare('validate', LISTING_NO_PUBLISHABLE_COLOR_MESSAGE, { title: titleEarly });
      if (earlyFail) return earlyFail;
    }
  }
  // 印花图仅站内展示 / 侵权检测，不自动拼接到货号与商家 SKU
  form = await enrichTiktokFormPrintNames(form);
  if (shopType) form = { ...form, shopType };
  // 商品库标题优先写入表单；title 变量仅用于进度展示时也必须回写，避免 payload 标题为空
  const resolvedTitle = String(form.title || titleHint || meta.title || '').trim()
    || String(titleHint || '').trim()
    || fixedUi('未命名商品');
  form = { ...form, title: resolvedTitle };
  const title = resolvedTitle;

  if (!form.categoryId) {
    const earlyFail = failPrepare('validate', fixedUi('缺少商品类目'), { title });
    if (earlyFail) return earlyFail;
  }
  if (!form.productTypeId) {
    const earlyFail = failPrepare('validate', fixedUi('缺少商品类型，请重新选择末级类目'), { title });
    if (earlyFail) return earlyFail;
  }
  if (!collectAllErrors && validationMessages.length) {
    return buildPrepareFailure({
      draftId,
      title,
      stage: 'validate',
      messages: validationMessages,
      meta,
    });
  }
  if (!form.categoryId || !form.productTypeId) {
    return buildPrepareFailure({
      draftId,
      title,
      stage: 'validate',
      messages: validationMessages,
      meta,
    });
  }

  let attrTpl;
  try {
    attrTpl = await resolveAttrTemplate(shopId, form.productTypeId, attrTplCache);
  } catch (ex) {
    return buildPrepareFailure({
      draftId,
      title,
      stage: 'validate',
      message: ex?.message || fixedUi('类目属性加载失败'),
      messages: validationMessages,
      meta,
    });
  }

  form = {
    ...form,
    productAttributes: alignAttributesToCategoryTemplate(form.productAttributes, attrTpl),
  };

  const colorAttr = attrTpl?.skcSaleAttrs?.[0] || null;
  const sizeAttr = attrTpl?.skuSaleAttrs?.[0] || null;
  const sizeMeasureAttrs = attrTpl?.sizeAttrs || [];

  const preflight = await runTiktokPublishPreflight({
    shopId,
    form,
    isEdit: false,
    attrTpl,
    shopType: form?.shopType || shopType || '',
    siteList,
  });
  if (!preflight.ok) {
    const earlyFail = failPrepare(
      'preflight',
      preflight.message || fixedUi('发布前校验未通过'),
      { title, field: preflight.field, section: preflight.section },
    );
    if (earlyFail) return earlyFail;
  }

  const skuPkgCaps = resolveSkuPackageCapabilities(preflight.standardRaw);
  const resolvedSiteList = preflight.siteList || siteList;
  // 市场地区：/publish/standard 回包优先（预售 / 字段上限按市场判定）
  const marketMeta = preflight.standardRaw || {};
  const marketRegion = String(
    form.region || marketMeta.region || marketMeta?.data?.region || '',
  ).trim().toUpperCase();
  const titleMaxLength = Number(marketMeta.titleMaxLength ?? marketMeta?.data?.titleMaxLength) > 0
    ? Number(marketMeta.titleMaxLength ?? marketMeta?.data?.titleMaxLength)
    : 0;
  // 类目规则：预售（allowed_special_product_types）等字段的开关来源
  const categoryRules = await resolveCategoryRules(shopId, form.categoryId, categoryRulesCache);
  const resolved = resolveShopPublishCurrencies({
    shopType: form?.shopType || shopType || '',
    siteList: resolvedSiteList,
    fillInStandard: preflight.standardRaw,
    preferred: form.currency,
    selectedSites: form.selectedSites,
  });
  const alignedCurrency = resolved.code;
  if (!alignedCurrency) {
    const currencyMessage = resolved.emptyWarning
      || (resolved.source === 'site-list'
        ? fixedUi('缺少店铺站点币种，请到店铺同步站点后再试')
        : fixedUi('缺少商品发布规范币种，请到店铺同步后再试'));
    const earlyFail = failPrepare('preflight', currencyMessage, { title });
    if (earlyFail) return earlyFail;
  } else {
    form = { ...form, currency: alignedCurrency, siteList: resolvedSiteList };
  }

  if (String(form.title || '').trim() && !String(form.titleEn || '').trim()) {
    try {
      const titleEn = await translateTiktokTitleToEnglish(form.title);
      form = { ...form, titleEn };
    } catch (error) {
      const earlyFail = failPrepare(
        'validate',
        error?.message || fixedUi('英文名称自动翻译失败'),
        { title, field: 'titleEn', section: 'basic' },
      );
      if (earlyFail) return earlyFail;
    }
  }

  const local = validateTiktokPublishForm(form, {
    isEdit: false,
    shopId,
    shopType: form?.shopType || shopType || '',
    attrTpl,
    colorAttr,
    sizeAttr,
    sizeMeasureAttrs,
    warehouseOptions: Array.isArray(warehouses) ? warehouses : [],
    skuPkgCaps,
    categoryRules,
    region: marketRegion,
  });
  if (!local.ok) {
    const earlyFail = failPrepare(
      'validate',
      local.message || fixedUi('表单校验未通过'),
      { title, field: local.field, section: local.section },
    );
    if (earlyFail) return earlyFail;
  }

  if (collectAllErrors && validationMessages.length) {
    return buildPrepareFailure({
      draftId,
      title,
      stage: 'validate',
      messages: validationMessages,
      meta,
    });
  }

  try {
    form = await ensureTiktokSizeChartForPublish(form, shopId);
  } catch (error) {
    const earlyFail = failPrepare(
      'validate',
      error?.message || fixedUi('尺码表处理失败'),
      { title, field: 'sizeChart', section: 'spec' },
    );
    if (earlyFail) return earlyFail;
  }

  const submitFormBase = buildSubmitForm(form, { colorAttr, sizeAttr, alignedCurrency });
  const submitForm = {
    ...submitFormBase,
    sizeChart: pruneSizeChartForSubmit(submitFormBase, sizeMeasureAttrs),
  };

  let payload;
  try {
    payload = buildTiktokPublishPayload(submitForm, {
      isEdit: false,
      sizeAttrs: sizeMeasureAttrs,
      skuPkgCaps,
      shopType: form?.shopType || shopType || '',
      siteList: resolvedSiteList,
      attrTpl,
      categoryRules,
      region: marketRegion,
      titleMaxLength,
    });
  } catch (buildErr) {
    return {
      ok: false,
      draftId,
      title,
      stage: 'build',
      message: buildErr?.message || fixedUi('无法组装发布参数'),
      ...meta,
    };
  }

  if (isTiktokTtsPublishMode()) {
    // 商品库/自动规则（omitIncompleteSkcs）：跳过店铺前置条件门禁，只跑 listing_check 内容核验，
    // 避免工作台未进 TikTok 工作区时被 warehouse/tax 等前置条件误拦。
    const listingCheck = await runTiktokListingPlatformCheck({
      shopId,
      ttsProduct: payload?.ttsProduct || null,
      payload,
      skipPrerequisites: Boolean(omitIncompleteSkcs),
    });
    if (!listingCheck.ok) {
      return {
        ok: false,
        draftId,
        title,
        stage: 'listing_check',
        message: listingCheck.message || fixedUi('平台发品核验未通过'),
        field: listingCheck.field,
        section: listingCheck.section,
        ...meta,
      };
    }
  }

  // 幂等键（idempotency_key）：核验通过、真正提交前才生成；listing_check 未收录该字段
  if (payload?.ttsProduct) {
    payload.ttsProduct.idempotency_key = createTiktokIdempotencyKey();
  }

  const sampleCurrency = samplePublishPayloadCurrency(payload);
  if (!sampleCurrency) {
    return {
      ok: false,
      draftId,
      title,
      stage: 'build',
      message: resolved.source === 'site-list'
        ? fixedUi('发品参数缺少售价币种（须来自店铺可售站点）')
        : fixedUi('发品参数缺少供货价币种（须来自商品发布规范）'),
      ...meta,
    };
  }

  return {
    ok: true,
    draftId,
    title,
    submitForm,
    payload,
    ...meta,
  };
}

/**
 * 单条草稿：拉详情后走与发品页相同的准备链路。
 */
export async function prepareDraftPublish({
  shopId,
  draftId,
  draftMeta = {},
  warehouses = [],
  attrTplCache,
  categoryRulesCache,
  shopType = '',
  siteList = null,
}) {
  const titleHint = draftMeta.title || draftMeta.supplierCode || fixedUiTpl('草稿#{0}', draftId);
  const detail = await tiktokPlatformAPI.getPublishDraft(shopId, draftId);
  return prepareFormPublish({
    shopId,
    form: detail?.form || {},
    titleHint,
    draftId,
    warehouses,
    attrTplCache,
    categoryRulesCache,
    shopType,
    siteList,
  });
}

function draftFormPayload(submitForm) {
  return {
    form: {
      ...submitForm,
      selectedSizeIds: (submitForm.selectedSizeIds?.length
        ? submitForm.selectedSizeIds
        : deriveSelectedSizeIds(submitForm.skcList)
      ).map((id) => String(id)),
    },
  };
}

/**
 * 保存草稿并调用 publish-or-edit（立即返回 job，不等待平台终态；与发品页入队一致）。
 */
export async function enqueuePreparedPublish({
  shopId,
  prepared,
  onHint,
}) {
  const { title, submitForm, payload } = prepared;
  let draftId = prepared.draftId;

  try {
    onHint?.(fixedUiTpl('正在保存草稿「{0}」…', title));
    if (draftId != null && draftId !== '') {
      await tiktokPlatformAPI.updatePublishDraft(shopId, draftId, draftFormPayload(submitForm));
    } else {
      const draftRes = await tiktokPlatformAPI.createPublishDraft(shopId, draftFormPayload(submitForm));
      if (draftRes?.id != null) draftId = String(draftRes.id);
    }
  } catch {
    // 与发品页一致：保存草稿失败不阻断提交
  }

  onHint?.(fixedUiTpl('正在提交「{0}」…', title));
  const params = draftId != null && draftId !== ''
    ? { draftId: Number(draftId) || draftId }
    : {};
  const job = await tiktokPlatformAPI.publishOrEditProduct(shopId, payload, params);
  return {
    ...prepared,
    draftId,
    job,
    jobId: job?.jobId || null,
  };
}

function finalizePublishJobResult(prepared, job) {
  const { draftId, title } = prepared;
  const jobId = job?.jobId || prepared.jobId || null;
  let platformRes = job?.response;
  if (!platformRes && job && (job.code != null || job.info != null)) {
    platformRes = job;
  }
  if (!platformRes && job?.status === 'failed') {
    return {
      ok: false,
      draftId,
      title,
      stage: 'publish',
      message: job?.error || job?.message || fixedUi('提交失败'),
      jobId,
      listingItemId: prepared.listingItemId,
    };
  }

  const res = platformRes || job?.response || job;
  const ok = isTiktokPublishSuccess(res)
    || job?.ok === true
    || String(job?.status || '').toLowerCase() === 'success';
  if (!ok) {
    return {
      ok: false,
      draftId,
      title,
      stage: 'publish',
      message: extractTiktokPublishFailMessage(res) || job?.error || job?.message || fixedUi('发布未成功'),
      jobId,
      listingItemId: prepared.listingItemId,
    };
  }

  return {
    ok: true,
    draftId,
    title,
    stage: 'publish',
    jobId,
    publishedSpu: job?.publishedSpu
      || extractTiktokPublishedSpu(res, prepared?.payload || {}, prepared?.submitForm?.spuName)
      || null,
    platformRes: res,
    form: prepared?.submitForm || null,
    message: fixedUi('发布成功，商品已上架'),
    listingItemId: prepared.listingItemId,
  };
}

/**
 * 提交并轮询至任务结束（草稿箱批量仍用此路径，同店串行）。
 */
export async function submitPreparedDraftPublish({
  shopId,
  prepared,
  onHint,
}) {
  let enqueued;
  try {
    enqueued = await enqueuePreparedPublish({ shopId, prepared, onHint });
  } catch (ex) {
    return {
      ok: false,
      draftId: prepared.draftId,
      title: prepared.title,
      stage: 'publish',
      message: ex?.message || fixedUi('提交失败'),
      listingItemId: prepared.listingItemId,
    };
  }

  let job = enqueued.job;
  const jobId = job?.jobId;
  if (jobId && !job?.done) {
    job = await waitGoodsSubmitJob(shopId, jobId, onHint);
  }
  return finalizePublishJobResult(enqueued, job);
}

/**
 * 仅轮询已入队任务，得到与 submitPreparedDraftPublish 相同结构的结果。
 */
export async function pollEnqueuedPublishResult({
  shopId,
  enqueued,
  onHint,
}) {
  let job = enqueued?.job;
  const jobId = job?.jobId || enqueued?.jobId;
  if (jobId && (!job || !job.done)) {
    job = await waitGoodsSubmitJob(shopId, jobId, onHint);
  }
  return finalizePublishJobResult(enqueued, job || enqueued?.job);
}

/**
 * @param {object} opts
 * @param {string|number} opts.shopId
 * @param {Array<{ id: string|number, title?: string, supplierCode?: string }>} opts.drafts
 * @param {number} [opts.concurrency=40]
 * @param {(progress: { phase: string, processed: number, total: number, message: string, last?: object }) => void} [opts.onProgress]
 * @returns {Promise<{ success: object[], failed: object[], total: number }>}
 */
export async function batchPublishDrafts({
  shopId,
  drafts,
  shopType = '',
  concurrency = TIKTOK_BATCH_PUBLISH_CONCURRENCY,
  onProgress,
}) {
  const list = (Array.isArray(drafts) ? drafts : []).filter((d) => d?.id != null);
  const total = list.length;
  if (!shopId || !total) {
    return { success: [], failed: [], total: 0 };
  }

  let warehouses = [];
  try {
    const wh = await tiktokPlatformAPI.getShopWarehouses(shopId, {
      warehouseType: 'SALES_WAREHOUSE',
    });
    const raw = Array.isArray(wh) ? wh : (Array.isArray(wh?.items) ? wh.items : []);
    warehouses = filterTiktokSalesWarehouses(raw);
  } catch {
    warehouses = [];
  }

  let siteList = null;
  try {
    const siteRes = await tiktokPlatformAPI.getShopSiteList(shopId);
    siteList = siteRes?.siteList ?? null;
  } catch {
    siteList = null;
  }

  const attrTplCache = new Map();
  const categoryRulesCache = new Map();
  const prepConcurrency = Math.max(1, Math.min(Number(concurrency) || TIKTOK_BATCH_PUBLISH_CONCURRENCY, TIKTOK_BATCH_PUBLISH_CONCURRENCY));

  onProgress?.({
    phase: 'preflight',
    processed: 0,
    total,
    message: fixedUiTpl('正在校验 0/{0}…（规则 / 属性 / SKU）', total),
  });

  const prepared = await mapWithConcurrency(
    list,
    prepConcurrency,
    async (draft) => {
      try {
        return await prepareDraftPublish({
          shopId,
          draftId: draft.id,
          draftMeta: draft,
          warehouses,
          attrTplCache,
          categoryRulesCache,
          shopType,
          siteList,
        });
      } catch (ex) {
        return {
          ok: false,
          draftId: draft.id,
          title: draft.title || draft.supplierCode || fixedUiTpl('草稿#{0}', draft.id),
          stage: 'prepare',
          message: ex?.message || fixedUi('准备发布失败'),
        };
      }
    },
    (done, all, result) => {
      onProgress?.({
        phase: 'preflight',
        processed: done,
        total: all,
        message: result?.ok
          ? fixedUiTpl('校验通过 {0}/{1}：{2}', done, all, result.title || '')
          : fixedUiTpl('校验失败 {0}/{1}：{2}', done, all, result?.message || ''),
        last: result,
      });
    },
  );

  const ready = prepared.filter((row) => row?.ok);
  const failed = prepared.filter((row) => row && !row.ok);
  const success = [];

  onProgress?.({
    phase: 'publish',
    processed: failed.length,
    total,
    message: ready.length
      ? fixedUiTpl('校验完成，开始发布 0/{0}…', ready.length)
      : fixedUi('全部未通过校验'),
  });

  // 同店发品任务 FIFO：提交串行，避免无 MQ 时多请求互锁超时
  for (let i = 0; i < ready.length; i += 1) {
    const item = ready[i];
    onProgress?.({
      phase: 'publish',
      processed: failed.length + success.length,
      total,
      message: fixedUiTpl('正在发布 {0}/{1}：{2}', i + 1, ready.length, item.title || item.draftId),
    });
    // eslint-disable-next-line no-await-in-loop
    const submitted = await submitPreparedDraftPublish({
      shopId,
      prepared: item,
      onHint: (hint) => {
        onProgress?.({
          phase: 'publish',
          processed: failed.length + success.length,
          total,
          message: hint || fixedUiTpl('正在发布 {0}/{1}', i + 1, ready.length),
        });
      },
    });
    if (submitted.ok) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await tiktokPlatformAPI.createPublishRecord(shopId, {
          form: submitted.form || item.submitForm || {},
          platformRes: submitted.platformRes || null,
          publishedSpu: submitted.publishedSpu || '',
          draftId: submitted.draftId || item.draftId,
          action: 'publish',
          listed: true,
        });
      } catch {
        /* ignore */
      }
      const cleanupId = submitted.draftId || item.draftId;
      if (cleanupId != null && cleanupId !== '') {
        try {
          // eslint-disable-next-line no-await-in-loop
          await tiktokPlatformAPI.deletePublishDraft(shopId, cleanupId);
        } catch {
          /* 清理失败不阻断批量 */
        }
      }
      success.push(submitted);
    } else failed.push(submitted);

    onProgress?.({
      phase: 'publish',
      processed: failed.length + success.length,
      total,
      message: submitted.ok
        ? fixedUiTpl('已发布 {0}/{1}：{2}', success.length, ready.length, submitted.title || '')
        : fixedUiTpl('发布失败：{0}', submitted.message || ''),
      last: submitted,
    });
  }

  return { success, failed, total };
}
